import { useEffect } from "react";
import { Plan, FarePlanState } from "@/src/components/desktop/srp/fareFamily/types";

type PlanWrapper = { plan: Plan; planIndex: number };

type Params = {
    plans: Plan[][];
    selectedFlightIndex: number;
    selectedPlanFareKey: string | null;
    selectedFilters: string[];
    setFarePlanState: React.Dispatch<React.SetStateAction<FarePlanState>>;
    currentFilteredPlans: PlanWrapper[];
};

function shallowEqualPlans(a: PlanWrapper[], b: PlanWrapper[]) {
    if (a.length !== b.length) return false;
    return a.every((item, i) => {
        const planA = item.plan;
        const planB = b[i]?.plan;
        if (!planB) return false;
        
        // Check key identifiers and prices
        return (
            planA.fareKey === planB.fareKey && 
            planA.displayPrice === planB.displayPrice &&
            planA.price === planB.price && // Base numeric price
            planA.basePrice === planB.basePrice // Base price for calculations
        );
    });
}

export const useFareFamilyPlanSync = ({
    plans,
    selectedFlightIndex,
    selectedPlanFareKey,
    selectedFilters,
    setFarePlanState,
    currentFilteredPlans,
}: Params) => {
    useEffect(() => {
        const currentPlans = plans[selectedFlightIndex] ?? [];
        let mapped: PlanWrapper[] = currentPlans.map((plan, i) => ({ plan, planIndex: i }));

        if (selectedFilters.length > 0) {
            mapped = mapped.filter(({ plan }) =>
                selectedFilters.every(label => plan.freeAmenities?.[label] === true)
            );
        }

        const currentSelected = mapped.find(({ plan }) => plan.fareKey === selectedPlanFareKey);
        // Auto-select the Regular fare — in the RT split view that is also the fare carrying the special-RT
        // combo's benefits, so both views default to the same "best deal" card.
        const bestDeal = mapped.find(({ plan }) => plan.ctBrandName === 'Regular');
        const fallbackKey = mapped[0]?.plan.fareKey ?? null;

        const nextFareKey = currentSelected
            ? selectedPlanFareKey
            : bestDeal?.plan.fareKey ?? fallbackKey;

        const plansChanged = !shallowEqualPlans(mapped, currentFilteredPlans);
        const fareKeyChanged = selectedPlanFareKey !== nextFareKey;

        if (plansChanged || fareKeyChanged) {
            setFarePlanState((prev: FarePlanState) => ({
                ...prev,
                filteredPlans: mapped,
                selectedPlanFareKey: nextFareKey,
            }));
        }
    }, [plans, selectedFlightIndex, selectedPlanFareKey, selectedFilters, currentFilteredPlans]);
};

// Reusable filter toggle handler
export const useFareFilterToggle = (
    setFarePlanState: React.Dispatch<React.SetStateAction<FarePlanState>>
) => {
    return (label: string, checked: boolean) => {
        setFarePlanState((prev: FarePlanState) => ({
            ...prev,
            selectedFilters: checked
                ? [...prev.selectedFilters, label]
                : prev.selectedFilters.filter((item: string) => item !== label),
        }));
    };
};
